import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { openWhatsApp } from "@/siteConfig";
import { Chip, LineInput, DualCTA, Eyebrow } from "@/components/bits";
import { useLang } from "@/i18n";

const ENTITY_TYPES = ["opt.entity.cong", "opt.entity.pe", "opt.entity.fo", "opt.entity.dev"];
const APPETITES = ["opt.app.land", "opt.app.hosp", "opt.app.health"];
const BANDS = ["opt.acqband.1", "opt.acqband.2", "opt.acqband.3"];

export const AcquirersSection = ({ onBook }) => {
  const { t } = useLang();
  const [form, setForm] = useState({
    entity_type: "",
    asset_appetite: [],
    capacity_band: "",
    name: "",
    phone: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const toggleAppetite = (a) =>
    setForm((f) => ({
      ...f,
      asset_appetite: f.asset_appetite.includes(a)
        ? f.asset_appetite.filter((x) => x !== a)
        : [...f.asset_appetite, a],
    }));

  const canSubmit =
    form.entity_type && form.capacity_band && form.name.trim() && form.phone.trim();

  const summary = [
    t("wa.acq.title"),
    `${t("wa.entity")}: ${t(form.entity_type)}`,
    `${t("wa.appetite")}: ${form.asset_appetite.length ? form.asset_appetite.map(t).join(", ") : "—"}`,
    `${t("wa.capacity")}: ${t(form.capacity_band)}`,
    `${t("wa.name")}: ${form.name}`,
    `${t("wa.phone")}: ${form.phone}`,
  ].join("\n");

  const submit = () => {
    openWhatsApp(summary);
    setSubmitted(true);
  };

  return (
    <section
      id="acquirers"
      data-testid="acquirers-section"
      className="py-16 md:py-24 border-t border-[#1A2A24]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-5">
          <Eyebrow>{t("acq.eyebrow")}</Eyebrow>
          <h2 className="font-display text-2xl md:text-3xl tracking-tight text-[#EDE7DA] mt-4 leading-[1.15]">
            {t("acq.h1a")}{" "}
            <em className="italic text-[#B8894B]">{t("acq.h1em")}</em>
          </h2>
          <p className="font-light text-sm md:text-base text-[#EDE7DA]/60 leading-relaxed mt-5 max-w-md">
            {t("acq.body")}
          </p>
          <div className="flex flex-col gap-3 mt-9">
            <Link
              to="/buyers"
              data-testid="acquirers-know-more-buyers"
              className="group inline-flex items-center gap-2.5 w-fit font-mono-label text-[11px] tracking-[0.2em] uppercase border border-[#B8894B]/60 text-[#B8894B] px-5 py-2.5 hover:bg-[#B8894B] hover:text-[#0E1613] transition-colors duration-300"
            >
              {t("km.buyers")}
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/institutional"
              data-testid="acquirers-know-more-institutional"
              className="group inline-flex items-center gap-2.5 w-fit font-mono-label text-[11px] tracking-[0.2em] uppercase border border-[#1A2A24] text-[#EDE7DA]/60 px-5 py-2.5 hover:border-[#B8894B] hover:text-[#B8894B] transition-colors duration-300"
            >
              {t("km.inst")}
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>

        <div className="md:col-span-7 border border-[#1A2A24] p-7 md:p-10">
          {submitted ? (
            <div data-testid="buyer-mandate-success">
              <p className="font-display text-xl text-[#EDE7DA] tracking-tight">
                {t("acq.success.h")}
              </p>
              <p className="font-light text-sm text-[#EDE7DA]/60 leading-relaxed mt-3">
                {t("acq.success.body")}
              </p>
              <div className="mt-7">
                <DualCTA
                  prefix="buyer-mandate"
                  waMessage={summary}
                  onBook={onBook}
                  waLabel={t("cta.waAgain")}
                  bookLabel={t("cta.bookCall")}
                />
              </div>
            </div>
          ) : (
            <div className="grid gap-7">
              <div>
                <span className="font-mono-label text-[10px] tracking-[0.25em] uppercase text-[#EDE7DA]/45">
                  {t("acq.entityLabel")}
                </span>
                <div className="flex flex-wrap gap-2.5 mt-3">
                  {ENTITY_TYPES.map((k) => (
                    <Chip
                      key={k}
                      testId={`acquirer-entity-${k.split(".").pop()}`}
                      active={form.entity_type === k}
                      onClick={() => setForm({ ...form, entity_type: k })}
                    >
                      {t(k)}
                    </Chip>
                  ))}
                </div>
              </div>
              <div>
                <span className="font-mono-label text-[10px] tracking-[0.25em] uppercase text-[#EDE7DA]/45">
                  {t("acq.appetiteLabel")}
                </span>
                <div className="flex flex-wrap gap-2.5 mt-3">
                  {APPETITES.map((k) => (
                    <Chip
                      key={k}
                      testId={`acquirer-appetite-${k.split(".").pop()}`}
                      active={form.asset_appetite.includes(k)}
                      onClick={() => toggleAppetite(k)}
                    >
                      {t(k)}
                    </Chip>
                  ))}
                </div>
              </div>
              <div>
                <span className="font-mono-label text-[10px] tracking-[0.25em] uppercase text-[#EDE7DA]/45">
                  {t("acq.bandLabel")}
                </span>
                <div className="flex flex-wrap gap-2.5 mt-3">
                  {BANDS.map((k) => (
                    <Chip
                      key={k}
                      testId={`acquirer-band-${k.split(".").pop()}`}
                      active={form.capacity_band === k}
                      onClick={() => setForm({ ...form, capacity_band: k })}
                    >
                      {t(k)}
                    </Chip>
                  ))}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-7">
                <LineInput
                  label={t("acq.nameLabel")}
                  testId="acquirer-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t("ph.principal")}
                />
                <LineInput
                  label={t("acq.phoneLabel")}
                  testId="acquirer-phone-input"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+91"
                />
              </div>
              <div>
                <button
                  type="button"
                  data-testid="buyer-mandate-submit"
                  disabled={!canSubmit}
                  onClick={submit}
                  className="font-mono-label text-[11px] tracking-[0.2em] uppercase border border-[#B8894B] text-[#B8894B] px-7 py-3 hover:bg-[#B8894B] hover:text-[#0E1613] transition-colors duration-300 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#B8894B]"
                >
                  {t("acq.submit")}
                </button>
                <p className="font-mono-label text-[10px] tracking-[0.15em] uppercase text-[#EDE7DA]/40 mt-4">
                  {t("cta.waNote")}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
